// The write layer for the golden-CSV DROP POINT.
//
// Calls the `reporting-golden-ingest` edge function directly from the browser with the
// signed-in user's JWT. That is the fn's own documented HUMAN DROP path: verify_jwt=true at
// the gateway, and in-body it binds an anon client to the caller's JWT and re-asserts
// can_review_reporting() (Investment ∪ Operations ∪ admins) before doing anything. The
// privileged writes then run on the fn's service-role client — the browser never holds a
// service key. supabase.functions.invoke attaches the session JWT automatically, so there is
// nothing to pass and nothing to store.
//
// TWO THINGS THIS FILE IS CAREFUL ABOUT
//
// 1. BYTE FIDELITY. The server takes content_sha256 over the bytes it receives, and that
//    hash IS the file's identity: it is the dedupe key (UNIQUE(period_label, kind,
//    content_sha256)), the Storage object name, and the immutable ledger's proof of what was
//    dropped. So the bytes we send must equal the bytes on disk. We decode with
//    `fatal:true` (a non-UTF-8 file THROWS instead of silently landing U+FFFD replacement
//    characters, which would both corrupt the data and change the hash) and `ignoreBOM:true`
//    (a leading U+FEFF is KEPT in the string so the server's TextEncoder reproduces the
//    original three BOM bytes). Anything that is not valid UTF-8 falls back to csv_base64,
//    which is byte-exact by construction. The server strips the BOM before parsing but
//    hashes the raw bytes, so both paths parse correctly AND hash identically to the file.
//
// 2. THE REAL FAILURE REASON. supabase.functions.invoke collapses every non-2xx into the
//    useless message "Edge Function returned a non-2xx status code". The gate name and the
//    reject reason — the only things that tell the owner what to fix — live in the response
//    BODY, so we read it back off error.context. Same trick as MeetingsReferenceData's
//    readFnError; kept local rather than shared because that one is embedded in a page.
//
// The outcome is a FLAT record with a `state` tag rather than a discriminated union: this
// project compiles with strictNullChecks:false and the ingest fn itself notes (index.ts:838)
// that literal-narrowing has bitten it under that flag. Nothing here needs narrowing to
// render, so nothing here depends on it.

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const GOLDEN_INGEST_FN = "reporting-golden-ingest";

// ----------------------------------------------------------------------------
// Reading the file
// ----------------------------------------------------------------------------
export interface CsvPayload {
  csv_text?: string;
  csv_base64?: string;
  /** How the file was read — surfaced so a binary fallback is visible, not silent. */
  encoding: "utf-8" | "binary";
  byte_size: number;
}

function bytesToBase64(bytes: Uint8Array): string {
  // Chunked: String.fromCharCode(...bytes) blows the call stack on anything sizeable.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const chunk = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
}

export async function readCsvPayload(file: File): Promise<CsvPayload> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  try {
    const text = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
    return { csv_text: text, encoding: "utf-8", byte_size: bytes.byteLength };
  } catch {
    // Not valid UTF-8 (a Windows-1252 export with a £ or an en-dash, typically). Send the
    // raw bytes instead of a lossy transcode.
    return { csv_base64: bytesToBase64(bytes), encoding: "binary", byte_size: bytes.byteLength };
  }
}

// ----------------------------------------------------------------------------
// The drop
// ----------------------------------------------------------------------------

/** A flag the ingest raised for this drop. Shape per index.ts:159 / the gate-policy row. */
export interface GoldenIngestFlag {
  check_name?: string;
  kind?: string;
  severity?: string;
  blocks_gate?: boolean;
  human_resolvable?: boolean;
  level?: string;
  pass?: boolean;
  detail?: Record<string, any>;
}

export type GoldenDropState = "parsed" | "duplicate" | "rejected" | "error";

export interface GoldenDropOutcome {
  state: GoldenDropState;
  source_id: string | null;
  sha256: string | null;
  kind: string | null;
  ingest_status: string | null;
  csv_format: string | null;
  rows_inserted: number | null;
  events_inserted: number | null;
  /** Gate name on a 422 (e.g. "fe_period_window"), null otherwise. */
  reject_gate: string | null;
  /** Why it was refused — the gate detail, or the transport/validation error. */
  reject_reason: string | null;
  http_status: number | null;
  row_warnings: string[];
  flags: GoldenIngestFlag[];
  /** The FE-native parse report (preamble, counts, unmapped names…) when present. */
  fe: Record<string, any> | null;
}

export interface GoldenDropInput {
  /** Optional link to the cycle this drop belongs to. */
  cycleId?: string | null;
  /** FROM THE CYCLE — never retyped by the human. */
  periodLabel: string;
  /** FROM THE CYCLE — never retyped by the human. */
  asOfDate: string;
  kind: string;
  basis: string;
  filename?: string | null;
  payload: CsvPayload;
}

function emptyOutcome(state: GoldenDropState): GoldenDropOutcome {
  return {
    state,
    source_id: null,
    sha256: null,
    kind: null,
    ingest_status: null,
    csv_format: null,
    rows_inserted: null,
    events_inserted: null,
    reject_gate: null,
    reject_reason: null,
    http_status: null,
    row_warnings: [],
    flags: [],
    fe: null,
  };
}

// Pull the status + JSON body back off an invoke() error. `context` is the Response for a
// FunctionsHttpError; for a relay/fetch error it is not, hence the duck-typing.
async function readInvokeError(
  error: any,
): Promise<{ status: number | null; body: Record<string, any> | null; message: string }> {
  const ctx = error?.context;
  const status = typeof ctx?.status === "number" ? ctx.status : null;
  let body: Record<string, any> | null = null;
  try {
    if (ctx && typeof ctx.clone === "function" && typeof ctx.json === "function") {
      body = (await ctx.clone().json()) as Record<string, any>;
    }
  } catch {
    /* the body was not JSON — fall through to the generic message */
  }
  const message = body?.detail ?? body?.error ?? error?.message ?? "Ingest failed";
  return { status, body, message: String(message) };
}

// The ingest's auth refusals are machine tokens. Translate the two a human can actually act
// on; everything else is passed through verbatim rather than paraphrased into something
// vaguer than what the server said.
function humaniseIngestError(raw: string): string {
  if (/forbidden_not_reporting_staff/i.test(raw)) {
    return "Refused: your account is not on the Investment or Operations team, so it cannot land golden sources.";
  }
  if (/^unauthenticated$/i.test(raw.trim())) {
    return "Refused: your session is not valid any more. Sign in again, then re-drop the file.";
  }
  return raw;
}

/** Shape whatever came back (2xx body, 4xx/5xx body, or a transport failure) into one record. */
function shapeOutcome(
  payload: Record<string, any> | null,
  fallbackKind: string,
  status: number | null,
  fallbackMessage: string | null,
): GoldenDropOutcome {
  const p = payload ?? {};

  if (p.ok === true && p.duplicate === true) {
    // Idempotent replay: these exact bytes are already landed under this (period, kind).
    // The duplicate response deliberately carries no counts — nothing was re-parsed.
    const out = emptyOutcome("duplicate");
    out.source_id = p.source_id ?? null;
    out.sha256 = p.sha256 ?? null;
    out.kind = p.kind ?? fallbackKind;
    out.ingest_status = p.ingest_status ?? null;
    return out;
  }

  if (p.ok === true) {
    const out = emptyOutcome("parsed");
    out.source_id = p.source_id ?? null;
    out.sha256 = p.sha256 ?? null;
    out.kind = p.kind ?? fallbackKind;
    out.ingest_status = p.ingest_status ?? null;
    out.csv_format = p.csv_format ?? null;
    out.rows_inserted = typeof p.rows_inserted === "number" ? p.rows_inserted : null;
    out.events_inserted = typeof p.events_inserted === "number" ? p.events_inserted : null;
    out.row_warnings = Array.isArray(p.row_warnings) ? p.row_warnings.map(String) : [];
    out.flags = Array.isArray(p.flags) ? (p.flags as GoldenIngestFlag[]) : [];
    out.fe = p.fe ?? null;
    return out;
  }

  // A named HARD gate refused the file (422). This is the case the owner must be able to
  // act on, so the gate and its detail are kept verbatim.
  if (p.gate) {
    const out = emptyOutcome("rejected");
    out.kind = fallbackKind;
    out.reject_gate = String(p.gate);
    out.reject_reason = String(p.detail ?? fallbackMessage ?? "Refused by an intake gate.");
    out.csv_format = p.csv_format ?? null;
    out.http_status = status;
    out.source_id = p.source_id ?? null;
    return out;
  }

  const out = emptyOutcome("error");
  out.kind = fallbackKind;
  out.reject_reason = humaniseIngestError(String(p.error ?? fallbackMessage ?? "Ingest failed."));
  out.http_status = status;
  out.source_id = p.source_id ?? null;
  return out;
}

/**
 * Drop ONE golden CSV. Never throws — every failure comes back as an outcome the UI can
 * render, because a silent throw at the drop point is indistinguishable from a file that
 * quietly did not land.
 */
export async function dropGoldenCsv(input: GoldenDropInput): Promise<GoldenDropOutcome> {
  const body: Record<string, any> = {
    period_label: input.periodLabel,
    kind: input.kind,
    basis: input.basis,
    as_of_date: input.asOfDate,
    // The only value GATE 1 allows; stated explicitly so the request is self-describing.
    source_system: "fe_quarter_end_csv",
  };
  if (input.cycleId) body.cycle_id = input.cycleId;
  if (input.filename) body.original_filename = input.filename;
  if (input.payload.csv_text != null) body.csv_text = input.payload.csv_text;
  if (input.payload.csv_base64 != null) body.csv_base64 = input.payload.csv_base64;
  // csv_format is deliberately NOT sent: the fn sniffs the FE Performance Table shape
  // itself, and declaring the wrong one is a rejection we would have caused.

  try {
    const { data, error } = await supabase.functions.invoke(GOLDEN_INGEST_FN, { body });
    if (error) {
      const read = await readInvokeError(error);
      return shapeOutcome(read.body, input.kind, read.status, read.message);
    }
    return shapeOutcome((data ?? null) as Record<string, any> | null, input.kind, 200, null);
  } catch (e: any) {
    const out = emptyOutcome("error");
    out.kind = input.kind;
    out.reject_reason = e?.message ?? String(e);
    return out;
  }
}

/**
 * An outcome for a failure that happened BEFORE the request left the browser (a file that
 * could not be read, typically). Shaped identically so the UI has one thing to render.
 */
export function localDropError(kind: string, message: string): GoldenDropOutcome {
  const out = emptyOutcome("error");
  out.kind = kind;
  out.reject_reason = message;
  return out;
}

/**
 * Thin hook wrapper: the same drop, plus cache invalidation so the cycle/list surfaces
 * re-read after something actually lands.
 */
export function useGoldenDrop(cycleId?: string | null) {
  const qc = useQueryClient();

  return useCallback(
    async (input: GoldenDropInput): Promise<GoldenDropOutcome> => {
      const outcome = await dropGoldenCsv(input);
      if (outcome.state === "parsed") {
        if (cycleId) qc.invalidateQueries({ queryKey: ["get_reporting_cycle", cycleId] });
        qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
      }
      return outcome;
    },
    [qc, cycleId],
  );
}
